import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { MdHelpOutline, MdOutlineVisibility } from "react-icons/md";

import styles from "./BulkEmailSender.module.scss";
import Axios from "../../_services/caller.service";
import Button from "../UiComponents/Button";
import Modal from "../UiComponents/modals/Modal";
import useModal from "../../hooks/useModal";
import CardHeaderPage from "../UiComponents/card/CardHeaderPage";
import { ROLE_VALUES, getRoleLabel } from "../../utils/constant/roles";

const ROLE_FILTERS = [
  { key: "all", value: "all" },
  { key: "owner", value: ROLE_VALUES.OWNER },
  { key: "admin", value: ROLE_VALUES.ADMIN },
  { key: "calibration", value: ROLE_VALUES.CALIBRATION },
  { key: "consultation", value: ROLE_VALUES.CONSULTATION },
];

// Classe CSS du tag de rôle (couleurs alignées sur les relances manuelles)
const roleTagClass = (role) => {
  switch (Number(role)) {
    case ROLE_VALUES.ADMIN:
      return styles.roleAdmin;
    case ROLE_VALUES.OWNER:
      return styles.roleOwner;
    case ROLE_VALUES.CALIBRATION:
      return styles.roleCalibration;
    case ROLE_VALUES.CONSULTATION:
      return styles.roleConsultation;
    default:
      return styles.roleDefault;
  }
};

// Palette de couleurs par groupe (identique à l'onglet Permissions)
const GROUP_COLORS = [
  "#7c3aed", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#ec4899", "#8b5cf6", "#14b8a6",
  "#f97316", "#6366f1", "#84cc16", "#06b6d4",
];


const BulkEmailSender = () => {
  const { t } = useTranslation();
  const { isShowing, toggle } = useModal();
  const { isShowing: isTutoShowing, toggle: toggleTuto } = useModal();
  const { isShowing: isPreviewShowing, toggle: togglePreview } = useModal();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("active");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    let active = true;
    Axios.get("/bulkEmail/users")
      .then((res) => {
        if (!active) return;
        setUsers(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (active) toast.error(t("bulk_email.load_error"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [t]);

  // Recuperation des templates Brevo (actifs)
  useEffect(() => {
    let active = true;
    Axios.get("/bulkEmail/templates")
      .then((res) => {
        if (!active) return;
        setTemplates(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (active) toast.error(t("bulk_email.templates_load_error"));
      })
      .finally(() => {
        if (active) setTemplatesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [t]);

  // Ouverture de l'apercu du template selectionne
  const handlePreview = () => {
    const id = Number(templateId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error(t("bulk_email.invalid_template"));
      return;
    }
    setPreview(null);
    setPreviewLoading(true);
    togglePreview();
    Axios.get(`/bulkEmail/templates/${id}/preview`)
      .then((res) => {
        setPreview(res.data?.data || null);
      })
      .catch(() => {
        toast.error(t("bulk_email.preview_error"));
      })
      .finally(() => setPreviewLoading(false));
  };

  // Liste des groupes disponibles
  const groups = useMemo(() => {
    const set = new Set(users.map((u) => u.groupType || "principale"));
    return Array.from(set).sort();
  }, [users]);

  // Couleur d'un groupe : "principale" en gris, les autres via la palette (par index)
  const getGroupColor = (group) => {
    if (!group || group === "principale") return "#9ca3af";
    const idx = groups.indexOf(group);
    return idx >= 0 ? GROUP_COLORS[idx % GROUP_COLORS.length] : "#9ca3af";
  };

  // Application des filtres (recherche + role + groupe + abonnement)
  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      // Filtre abonnement : actifs uniquement / inactifs uniquement / tous
      if (subFilter === "active" && !u.hasActiveSubscription) {
        return false;
      }
      if (subFilter === "inactive" && u.hasActiveSubscription) {
        return false;
      }
      if (roleFilter !== "all" && Number(u.role) !== Number(roleFilter)) {
        return false;
      }
      if (groupFilter !== "all" && (u.groupType || "principale") !== groupFilter) {
        return false;
      }
      if (term) {
        const haystack = `${u.displayName} ${u.email}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, groupFilter, subFilter]);

  // Nombre d'utilisateurs avec un abonnement actif
  const activeSubCount = useMemo(
    () => users.filter((u) => u.hasActiveSubscription).length,
    [users]
  );

  // Nombre d'utilisateurs sans abonnement actif
  const inactiveSubCount = useMemo(
    () => users.filter((u) => !u.hasActiveSubscription).length,
    [users]
  );

  const toggleUser = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedIds.has(u.id));

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openConfirm = () => {
    const id = Number(templateId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error(t("bulk_email.invalid_template"));
      return;
    }
    if (selectedIds.size === 0) {
      toast.error(t("bulk_email.no_recipient"));
      return;
    }
    toggle();
  };

  const handleSend = () => {
    setSending(true);
    Axios.post("/bulkEmail/send", {
      templateId: Number(templateId),
      userIds: Array.from(selectedIds),
    })
      .then((res) => {
        const { sent = 0, failed = 0, testMode } = res.data || {};
        if (failed > 0) {
          toast.warning(
            t("bulk_email.sent_with_errors", { sent, failed })
          );
        } else {
          toast.success(t("bulk_email.sent_success", { sent }));
        }
        if (testMode) toast.info(t("bulk_email.test_mode_notice"));
        clearSelection();
        toggle();
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || t("bulk_email.send_error");
        toast.error(msg);
      })
      .finally(() => setSending(false));
  };

  // Étapes du tutoriel (clés i18n -> traductions)
  const tutoSteps = [
    "bulk_email.tuto.step_template",
    "bulk_email.tuto.step_filter",
    "bulk_email.tuto.step_select",
    "bulk_email.tuto.step_send",
    "bulk_email.tuto.step_confirm",
  ];

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <CardHeaderPage text={t("bulk_email.title")} />
        <button
          type="button"
          className={styles.helpButton}
          onClick={toggleTuto}
          aria-label={t("bulk_email.tuto.open")}
          title={t("bulk_email.tuto.open")}
        >
          <MdHelpOutline size={20} />
          <span>{t("bulk_email.tuto.button")}</span>
        </button>
      </div>

      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.search}
          placeholder={t("bulk_email.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.select}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          {ROLE_FILTERS.map((r) => (
            <option key={r.key} value={r.value}>
              {r.key === "all"
                ? t("bulk_email.all_roles")
                : t(getRoleLabel(r.value))}
            </option>
          ))}
        </select>

        <select
          className={styles.select}
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
        >
          <option value="all">{t("bulk_email.all_groups")}</option>
          {groups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>

        <select
          className={styles.select}
          value={subFilter}
          onChange={(e) => setSubFilter(e.target.value)}
        >
          <option value="active">{t("bulk_email.sub_filter_active")}</option>
          <option value="inactive">{t("bulk_email.sub_filter_inactive")}</option>
          <option value="all">{t("bulk_email.sub_filter_all")}</option>
        </select>

        <div className={styles.subCounts}>
          <span className={styles.subInfo}>
            {t("bulk_email.active_sub_count", { count: activeSubCount })}
          </span>
          <span className={styles.subInfoInactive}>
            {t("bulk_email.inactive_sub_count", { count: inactiveSubCount })}
          </span>
        </div>
      </div>

      <div className={styles.templateRow}>
        <label htmlFor="bulkTemplateId">{t("bulk_email.template_label")}</label>
        <select
          id="bulkTemplateId"
          className={styles.templateSelect}
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          disabled={templatesLoading}
        >
          <option value="">
            {templatesLoading
              ? t("bulk_email.templates_loading")
              : t("bulk_email.template_placeholder")}
          </option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {`#${tpl.id} — ${tpl.name}`}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={styles.previewButton}
          onClick={handlePreview}
          disabled={!templateId}
          title={t("bulk_email.preview_button")}
        >
          <MdOutlineVisibility size={18} />
          <span>{t("bulk_email.preview_button")}</span>
        </button>
        <span className={styles.selectedCount}>
          {t("bulk_email.selected_count", { count: selectedIds.size })}
        </span>
        <Button
          text={t("bulk_email.send_button")}
          styles="btnOnPrimary"
          onPress={openConfirm}
          disabled={selectedIds.size === 0 || !templateId}
        />
      </div>

      {loading ? (
        <p className={styles.info}>{t("bulk_email.loading")}</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                    aria-label={t("bulk_email.select_all")}
                  />
                </th>
                <th>{t("bulk_email.col_name")}</th>
                <th>{t("bulk_email.col_email")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr
                  key={u.id}
                  className={selectedIds.has(u.id) ? styles.selectedRow : ""}
                  onClick={() => toggleUser(u.id)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleUser(u.id)}
                    />
                  </td>
                  <td>
                    <div className={styles.nameCell}>
                      <span className={styles.userName}>{u.displayName}</span>
                      {u.role != null && (
                        <span className={`${styles.roleTag} ${roleTagClass(u.role)}`}>
                          {t(getRoleLabel(u.role))}
                        </span>
                      )}
                      {u.hasActiveSubscription && (
                        <span className={styles.subTag}>
                          {t("bulk_email.sub_active")}
                        </span>
                      )}
                      <span
                        className={styles.groupTag}
                        style={{
                          color: getGroupColor(u.groupType),
                          backgroundColor: `${getGroupColor(u.groupType)}1f`,
                          borderColor: getGroupColor(u.groupType),
                        }}
                      >
                        {u.groupType}
                      </span>
                    </div>
                  </td>
                  <td>{u.email}</td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={3} className={styles.info}>
                    {t("bulk_email.no_result")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isShowing={isPreviewShowing}
        hide={togglePreview}
        title={t("bulk_email.preview_title")}
        className={styles.previewModal}
      >
        <div className={styles.previewBody}>
          {previewLoading ? (
            <p className={styles.info}>{t("bulk_email.preview_loading")}</p>
          ) : preview ? (
            <>
              <div className={styles.previewMeta}>
                <span className={styles.previewName}>
                  {`#${preview.id} — ${preview.name}`}
                </span>
                {preview.subject && (
                  <span className={styles.previewSubject}>
                    {t("bulk_email.preview_subject", { subject: preview.subject })}
                  </span>
                )}
              </div>
              <iframe
                className={styles.previewFrame}
                title={t("bulk_email.preview_title")}
                sandbox=""
                srcDoc={preview.htmlContent || ""}
              />
            </>
          ) : (
            <p className={styles.info}>{t("bulk_email.preview_error")}</p>
          )}
        </div>
      </Modal>

      <Modal
        isShowing={isShowing}
        hide={toggle}
        title={t("bulk_email.confirm_title")}
      >
        <div className={styles.confirmBody}>
          <p>
            {t("bulk_email.confirm_text", {
              count: selectedIds.size,
              templateId,
            })}
          </p>
          <div className={styles.confirmActions}>
            <Button
              text={t("common.cancel")}
              styles="btnOutline"
              onPress={toggle}
              disabled={sending}
            />
            <Button
              text={sending ? t("bulk_email.sending") : t("bulk_email.confirm_send")}
              styles="btnPrimary"
              onPress={handleSend}
              disabled={sending}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isShowing={isTutoShowing}
        hide={toggleTuto}
        title={t("bulk_email.tuto.title")}
      >
        <div className={styles.tutoBody}>
          <p className={styles.tutoIntro}>{t("bulk_email.tuto.intro")}</p>
          <ol className={styles.tutoList}>
            {tutoSteps.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ol>
          <p className={styles.tutoNote}>{t("bulk_email.tuto.note")}</p>
          <div className={styles.confirmActions}>
            <Button
              text={t("bulk_email.tuto.got_it")}
              styles="btnPrimary"
              onPress={toggleTuto}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BulkEmailSender;
